import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Accessibility context — global text-size scale for elderly / low-vision users.
 *
 * Three levels:
 *   "normal" → 1.00× (default)
 *   "large"  → 1.18×
 *   "xl"     → 1.40×
 *
 * Applied via CSS variable on <html>: `--a11y-scale`. The :root rule in
 * index.css multiplies the base font-size by this variable, so every `rem`
 * across the app scales automatically. Tap targets, line-height and gaps are
 * boosted by CSS class on `html.a11y-large` / `html.a11y-xl`.
 *
 * Persisted in localStorage so iPhone Home-Screen PWAs retain the setting.
 */
const SCALE = { normal: 1.0, large: 1.18, xl: 1.4 };
const STORAGE_KEY = "lasovita.a11y.text_size";

const AccessibilityContext = createContext(null);

export function AccessibilityProvider({ children }) {
  const [textSize, setTextSize] = useState(() => {
    if (typeof window === "undefined") return "normal";
    return localStorage.getItem(STORAGE_KEY) || "normal";
  });

  useEffect(() => {
    const html = document.documentElement;
    const scale = SCALE[textSize] ?? 1.0;
    html.style.setProperty("--a11y-scale", String(scale));
    html.classList.remove("a11y-normal", "a11y-large", "a11y-xl");
    html.classList.add(`a11y-${textSize}`);
    localStorage.setItem(STORAGE_KEY, textSize);
  }, [textSize]);

  const cycle = () => {
    setTextSize((cur) => {
      if (cur === "normal") return "large";
      if (cur === "large") return "xl";
      return "normal";
    });
  };

  return (
    <AccessibilityContext.Provider value={{ textSize, setTextSize, cycle }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error("useAccessibility must be used within AccessibilityProvider");
  return ctx;
}
